import React, { useState, useEffect } from 'react';
import { AuthProvider } from './context/AuthContext';
import { SiteProvider } from './context/SiteContext';
import { PublicWebsiteApp } from './public/PublicWebsiteApp';
import { StandalonePublicWebsiteEngine } from './components/live/StandalonePublicWebsiteEngine';
import { CustomDomainResolver } from './components/live/CustomDomainResolver';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { RESERVED_ROUTES } from './utils/security';

interface RouteInfo {
  isCustomDomainHost: boolean;
  isPublicSite: boolean;
  target?: string;
  isPreview: boolean;
}

function resolveCurrentRoute(): RouteInfo {
  if (typeof window === 'undefined') {
    return { isCustomDomainHost: false, isPublicSite: false, isPreview: false };
  }

  const host = window.location.hostname.toLowerCase();
  const pathname = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  const siteParam = params.get('site');
  const domainParam = params.get('domain');

  // 1. Explicit query parameter override (preview / live testing in dashboard)
  if (siteParam || domainParam) {
    return {
      isCustomDomainHost: false,
      isPublicSite: true,
      target: siteParam || domainParam || undefined,
      isPreview: true,
    };
  }

  // 2. Hostname Check: Is this the SaaS platform host or a custom domain?
  const isPlatformHost =
    host === 'adiosoft.com' ||
    host === 'www.adiosoft.com' ||
    host.endsWith('.adiosoft.com') ||
    host.endsWith('.web.app') ||
    host.endsWith('.firebaseapp.com') ||
    host.includes('localhost') ||
    host.includes('127.0.0.1') ||
    host.endsWith('.run.app');

  // If hostname is a customer's custom domain (e.g., sunfacepool.com)
  if (!isPlatformHost) {
    return {
      isCustomDomainHost: true,
      isPublicSite: true,
      target: host,
      isPreview: false,
    };
  }

  // 3. Path-based Website Slug Resolution on Platform Host
  // e.g. https://adiosoft.com/sunface-pool
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0]?.toLowerCase();

  if (firstSegment && !RESERVED_ROUTES.has(firstSegment)) {
    return {
      isCustomDomainHost: false,
      isPublicSite: true,
      target: firstSegment,
      isPreview: false,
    };
  }

  // Regular SaaS application (Dashboard, Builder, Marketing, etc.)
  return { isCustomDomainHost: false, isPublicSite: false, isPreview: false };
}

export default function App() {
  const [routeInfo, setRouteInfo] = useState<RouteInfo>(resolveCurrentRoute);

  useEffect(() => {
    const handlePopState = () => {
      setRouteInfo(resolveCurrentRoute());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // 1. Custom Domain Public Resolution (Rule 12):
  // When accessed via customer's custom domain (e.g. sunfacepool.com),
  // render CustomDomainResolver directly without redirects or SaaS shell
  if (routeInfo.isCustomDomainHost && routeInfo.target) {
    return <CustomDomainResolver hostname={routeInfo.target} />;
  }

  // 2. Platform-hosted public site slug resolution (e.g. adiosoft.com/my-site or ?site=xyz)
  if (routeInfo.isPublicSite) {
    return (
      <ErrorBoundary
        fallbackTitle="Web Sitesi Yüklenirken Bir Hata Oluştu"
        fallbackDescription="Sayfa içeriği görüntülenirken beklenmeyen bir hata meydana geldi. Lütfen sayfayı yenileyiniz."
      >
        <StandalonePublicWebsiteEngine
          initialTarget={routeInfo.target}
          onExitPreview={
            routeInfo.isPreview
              ? () => {
                  setRouteInfo({ isCustomDomainHost: false, isPublicSite: false, isPreview: false });
                  const url = new URL(window.location.href);
                  url.searchParams.delete('site');
                  url.searchParams.delete('domain');
                  window.history.pushState({}, '', url.pathname);
                }
              : undefined
          }
        />
      </ErrorBoundary>
    );
  }

  // 3. Main Adiosoft SaaS Platform (Dashboard, Builder, Marketing)
  return (
    <ErrorBoundary
      fallbackTitle="Adiosoft Platform Yüklenirken Bir Hata Oluştu"
      fallbackDescription="Uygulama açılırken beklenmeyen bir hata meydana geldi. Lütfen sayfayı yenileyiniz."
    >
      <AuthProvider>
        <SiteProvider>
          <PublicWebsiteApp />
        </SiteProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
